import { useQuery } from "@tanstack/react-query";
import { fetchRunSuggestions, type RunSuggestion } from "@/services/testing";
import { useAuthStoreV1 } from "@/stores/authStore_v1";

/** Suggestions are derived from a finished run and never change, so they can be cached hard. */
const SUGGESTIONS_STALE_MS = 30 * 60 * 1000;

/**
 * "What to test next" suggestions for a finished run.
 *
 * Previously each RunResultCard fetched these itself in a `useEffect`, so every re-render of the chat
 * tree (which happens on any testing-store change) re-issued one network call per visible result
 * card. Going through react-query dedupes concurrent callers, caches per run id, and survives the
 * card unmounting when the user switches sessions and comes back.
 *
 * Resolves to an empty list on failure by design: this block degrades to silence rather than showing
 * the user an error for an optional enhancement.
 */
export function useRunSuggestions(runId: string | null) {
	const token = useAuthStoreV1((s) => s.token);
	const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
	return useQuery<RunSuggestion[]>({
		queryKey: ["testingRunSuggestions", runId, orgId],
		queryFn: () => fetchRunSuggestions(runId!).catch(() => []),
		enabled: !!(token && orgId && runId),
		staleTime: SUGGESTIONS_STALE_MS,
		gcTime: SUGGESTIONS_STALE_MS,
		refetchOnWindowFocus: false,
		retry: false,
		// Optional enhancement — a failure must never raise the global error toast.
		meta: { silent: true },
	});
}
